import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, Crown, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { startCheckout } from "@/pages/PaymentPages";
import { STRIPE_LINK_SCAN_20 } from "@/lib/stripeLinks";

export default function BillingPage() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const activate = async () => {
    try { await startCheckout("surveillance_monthly"); }
    catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de la création du paiement";
      toast.error(msg);
    }
  };

  const dl = async (o) => {
    const r = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `${o.invoice_number}.pdf`; a.click();
  };

  const quotaPct = user ? Math.min(100, (user.quota_used / user.quota_max) * 100) : 0;
  const isSurv = user?.plan === "surveillance";

  return (
    <div className="space-y-10" data-testid="billing-page">
      <div>
        <p className="eyebrow mb-1">Espace client</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">{t("backoffice.title")}</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">{t("billing.intro")}</p>
      </div>

      {/* Orders */}
      <section>
        <h2 className="text-2xl font-serif tracking-tight mb-4">{t("backoffice.orders")} ({orders.length})</h2>
        <div className="hairline">
          {orders.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">Aucune commande pour l'instant.</p>}
          {orders.map((o, i) => (
            <div key={o.id} className={`p-5 grid md:grid-cols-[100px_1fr_120px_120px_auto] gap-4 items-center ${i > 0 ? "border-t border-border" : ""}`} data-testid={`order-${o.id}`}>
              <span className="font-mono text-xs text-muted-foreground">{o.created_at.slice(0,10)}</span>
              <div>
                <p className="font-serif">{o.label}</p>
                <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{o.invoice_number}</p>
              </div>
              <span className="font-serif text-lg">{o.total_eur.toFixed(2)} €</span>
              <span className={`text-xs font-mono px-2 py-1 uppercase rounded-lg w-fit ${o.status === "paid" ? "tag-green" : "tag-orange"}`}>{o.status}</span>
              <Button size="sm" variant="outline" onClick={() => dl(o)} className="rounded-lg" data-testid={`download-invoice-${o.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> {t("backoffice.download")}
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
